import { useEffect, useState, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectContext, setContext } from '../../store/context'
import { useYourStory } from './yourStory'
import { useDatabase } from './database'

export function useDocumentContext(tool, pullFrom) {
  const dispatchAction = useDispatch()
  const contextData = useSelector(selectContext)
  const [docContext, setDocContext] = useState(contextData[pullFrom ?? tool] ?? [])
  const [databases, setDatabases] = useState([])

  const [isModalVisible, setIsModalVisible] = useState(false)
  const [savedState, setSavedState] = useState({ docContext, databases })

  const { data: storyData, isLoading: storyLoading } = useYourStory()
  const { data: availableDatabases, isLoading: isDatabaseLoading } = useDatabase()

  const stories = useMemo(() => {
    const data =
      storyData?.map((story) => ({
        id: story.id,
        label: story.templateName,
        content: story.storyText,
      })) || []

    return { isLoading: storyLoading, data }
  }, [storyData, storyLoading])

  // Update store
  useEffect(() => {
    if (docContext) dispatchAction(setContext({ tool: tool, data: docContext }))
  }, [docContext, dispatchAction, tool])

  const addDocument = useCallback(
    ({ label, type, content, id, description, invalid = false, isChunk = true, metadata }) => {
      setDocContext((prev) => {
        if (prev.some((item) => item.id === id)) return prev
        return [...prev, { label, type, content, id, description, invalid, isChunk, metadata }]
      })
    },
    []
  )

  const addDatabase = useCallback(({ name, id, descriptionKeyName }) => {
    setDatabases((prev) => {
      if (prev.some((item) => item.id === id)) return prev
      return [...prev, { name, id, descriptionKeyName, type: 'database' }]
    })
  }, [])

  const removeDocument = useCallback((id) => {
    setDocContext((prev) => prev.filter((doc) => doc.id !== id))
  }, [])

  const removeDatabase = useCallback((id) => {
    setDatabases((prev) => prev.filter((db) => db.id !== id))
  }, [])

  const toggleEditDocument = useCallback((id) => {
    setDocContext((prev) =>
      prev.map((item) => (item.id === id ? { ...item, edit: !item.edit } : item))
    )
  }, [])

  const toggleChunkDocument = useCallback((id) => {
    setDocContext((prev) =>
      prev.map((item) => (item.id === id ? { ...item, isChunk: !item.isChunk } : item))
    )
  }, [])

  const removeEmptyDocuments = useCallback(() => {
    setDocContext((prev) => prev.filter((doc) => doc.content))
  }, [])

  const openModal = useCallback(() => {
    setSavedState({ docContext, databases })
    setIsModalVisible(true)
  }, [docContext, databases])

  const closeModal = useCallback(
    ({ saveChanges = true }) => {
      if (!saveChanges) {
        setDocContext(savedState.docContext)
        setDatabases(savedState.databases)
      }
      setIsModalVisible(false)
    },
    [savedState]
  )

  const clearContext = useCallback(() => {
    setDocContext([])
  }, [])

  const removeDocumentsOfType = useCallback((type) => {
    setDocContext((prev) => prev.filter((doc) => doc.type !== type))
  }, [])

  const returnValue = useMemo(
    () => ({
      docContext,
      stories,
      databases,
      setDatabases,
      setDocContext,
      addDocument,
      removeDocument,
      toggleEditDocument,
      toggleChunkDocument,
      removeEmptyDocuments,
      removeDocumentsOfType,
      removeDatabase,
      clearContext,
      isModalVisible,
      openModal,
      closeModal,
      availableDatabases,
      isDatabaseLoading,
      addDatabase,
    }),
    [
      docContext,
      stories,
      databases,
      addDocument,
      removeDocument,
      toggleEditDocument,
      toggleChunkDocument,
      removeEmptyDocuments,
      removeDocumentsOfType,
      removeDatabase,
      clearContext,
      isModalVisible,
      openModal,
      closeModal,
      availableDatabases,
      isDatabaseLoading,
      addDatabase,
    ]
  )

  return returnValue
}
